import { useMemo, useState } from 'react';

import { API } from 'api';
import type { OverlayQuestionProps } from './types';
import { apiBasePath, apiName } from 'assets/constants';
import { showOverlaySubmitErrorToast } from './toast';
import { DOMAINS, IMPORTANCE } from '@repo/monitoring';

import { ButtonSizes, Typography } from '@repo/ui';
import { Button, ButtonColors, ButtonVariants } from 'components';
import { NpsScale } from '../../NpsScale/NpsScale';

import styles from '../AudienceFeedbackOverlay.module.css';

const TITLE =
  'How likely are you to recommend Tiny Health to friends or family?';

const NpsQuestion = ({ kitId, feedbackType, onDone }: OverlayQuestionProps) => {
  const [score, setScore] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSubmitDisabled = useMemo(() => score == null, [score]);

  const onSubmit = async () => {
    if (isSubmitting || score == null) return;

    setIsSubmitting(true);
    try {
      await API.post(
        apiName,
        `${apiBasePath}/nps/${kitId}`,
        {
          rating: Number(score),
          feedback: '',
          source: 'overlay',
        },
        true,
        undefined,
        {
          tags: {
            domain: DOMAINS.FEEDBACK,
            importance: IMPORTANCE.MEDIUM,
            resource: 'nps',
          },
          context: {
            kitId,
            component: 'NpsQuestion',
            feedbackType,
            source: 'overlay',
            rating: score,
          },
        },
      );
    } catch (err) {
      console.error('Failed to submit AudienceFeedback/NpsQuestion', err);
      showOverlaySubmitErrorToast();
    } finally {
      setIsSubmitting(false);
    }

    onDone();
  };

  return (
    <div className={styles.content}>
      <Typography variant='body-m' className={styles.primaryText}>
        {TITLE}
      </Typography>

      <Typography variant='label' className={styles.secondaryText}>
        {'(1 = extremely unlikely, 10 = extremely likely)'}
      </Typography>

      <div className={styles.inputRow}>
        <NpsScale label={TITLE} value={score} onChange={setScore} />
      </div>

      <div className={styles.actionsRow}>
        <Button
          label='Next'
          onClick={onSubmit}
          disabled={isSubmitDisabled || isSubmitting}
          loading={isSubmitting}
          variant={ButtonVariants.SOLID}
          color={ButtonColors.GREEN}
          size={ButtonSizes.S}
        />
      </div>
    </div>
  );
};

export default NpsQuestion;
